/**
 * Catalogo capacità Guardian Family VIP.
 *
 * FONTE DI VERITÀ: deriva dal registro feature reale del prodotto, non da copy
 * inventato. Le chiavi e i default corrispondono a:
 *   - BACKEND  app/core/features.py  (FEATURE_DEFAULTS / FEATURE_CONFIG_DEFAULTS)
 *   - ANDROID  core/common/FeatureFlags.kt  (KNOWN / DEFAULTS)
 *   - WEBSITE  lib/api-types.ts  (FEATURE_KEYS / FEATURE_LABELS)
 * Se una capacità viene aggiunta/rimossa lì, aggiornare questo file.
 *
 * `key`      = nome feature nel registro (usato per coerenza e test)
 * `gate`     = "on" attiva di default · "opt" off di default, il genitore la attiva
 *              · "on-demand" scatta solo quando il genitore invia il comando
 * `sensitive`= capacità ad alta sensibilità (mostrata con badge dedicato)
 */

// Cosa vede il genitore, raggruppato per "tipo di dato" (stile ClevGuard:
// elenca ogni categoria intercettata, una per una, con icona e dettaglio).
export const DATA_CATEGORIES = [
  {
    key: "chat_capture",
    icon: "message",
    tag: "Chat",
    title: "Chat e messaggistica",
    short: "WhatsApp e altre app di messaggistica.",
    detail:
      "Conversazioni complete di WhatsApp: messaggi ricevuti e inviati, anche quelli eliminati e quelli che non generano notifica. Più inbox unificata delle app social installate (TikTok, Telegram, Instagram e altre).",
    gate: "on",
    link: { href: "/funzioni/whatsapp/", label: "Approfondisci: monitoraggio WhatsApp" },
    points: [
      "Chat WhatsApp completa (in arrivo e in uscita)",
      "Messaggi eliminati riconciliati e recuperati",
      "Inbox unificata multi-app (TikTok, Telegram, …)",
      "Contatti, orari e anteprime per ogni conversazione",
    ],
  },
  {
    key: "call_log",
    icon: "phone",
    tag: "Chiamate",
    title: "Registro chiamate",
    short: "Chi chiama, quando, per quanto tempo.",
    detail:
      "Storico completo delle chiamate del dispositivo: numero, nome in rubrica, direzione (in ingresso / in uscita / persa), durata e data-ora.",
    gate: "on",
    points: [
      "Numero, nome, direzione e durata",
      "Chiamate perse evidenziate",
      "Finestra temporale configurabile (giorni)",
      "Esportazione del registro",
    ],
  },
  {
    key: "live_video",
    icon: "camera",
    tag: "Live",
    title: "Fotocamera in diretta",
    short: "Guarda cosa inquadra la camera, ora.",
    detail:
      "Attiva la fotocamera del dispositivo da remoto e guarda il flusso video in diretta (stream MJPEG). Scatto foto on-demand dalla camera anteriore o posteriore, con selettore di qualità.",
    gate: "on-demand",
    sensitive: false,
    points: [
      "Streaming video live dal dispositivo",
      "Scatto foto on-demand (fronte / retro)",
      "Contatore fps e frame in tempo reale",
      "Comando inviato solo dal genitore",
    ],
  },
  {
    key: "ambient_audio",
    icon: "mic",
    tag: "Audio",
    title: "Ascolto ambientale",
    short: "Registra l'ambiente intorno al telefono.",
    detail:
      "Registrazioni audio programmate dell'ambiente in cui si trova il dispositivo. Con l'audio in diretta (opt-in) puoi ascoltare il microfono in streaming, anche durante una chiamata.",
    gate: "on",
    sensitive: true,
    points: [
      "Registrazioni ambientali schedulate",
      "Audio in diretta su comando (opt-in)",
      "Riproduzione e download dal pannello",
      "Resta attivo anche in chiamata",
    ],
  },
  {
    key: "screenshots",
    icon: "monitor",
    tag: "Schermo",
    title: "Screenshot del dispositivo",
    short: "Fotografia lo schermo, quando vuoi.",
    detail:
      "Cattura lo schermo del dispositivo su comando o in modo schedulato: vedi esattamente cosa sta guardando, chattando o giocando in quel momento.",
    gate: "on",
    points: [
      "Screenshot on-demand e schedulati",
      "Archivio consultabile nella dashboard",
      "Contesto completo dello schermo",
      "Nessuna cattura continua se non la attivi",
    ],
  },
  {
    key: "location",
    icon: "map-pin",
    tag: "GPS",
    title: "Posizione e aree sicure",
    short: "Dove si trova, in tempo reale.",
    detail:
      "Posizione in tempo reale e storico degli spostamenti, con luoghi sicuri e perimetri (geofence) che generano un avviso di arrivo/uscita.",
    gate: "on",
    points: [
      "Posizione live + storico percorsi",
      "Aree sicure con alert di ingresso/uscita",
      "Mappa nella dashboard",
      "Richiedi posizione immediata su comando",
    ],
  },
  {
    key: "sms_monitoring",
    icon: "message-square",
    tag: "SMS",
    title: "Messaggi SMS",
    short: "Testi, mittenti e orari degli SMS.",
    detail:
      "Lettura dei messaggi SMS del dispositivo: contenuto, numero del mittente/destinatario, data e ora.",
    gate: "on",
    points: [
      "Contenuto degli SMS in chiaro",
      "Mittente / destinatario e orario",
      "SMS in ingresso e in uscita",
      "Integrato nella vista messaggi",
    ],
  },
  {
    key: "contacts",
    icon: "users",
    tag: "Rubrica",
    title: "Rubrica e contatti",
    short: "Chi c'è nella rubrica del telefono.",
    detail:
      "Lettura dei contatti salvati sul dispositivo: nome, numero di telefono e contatti preferiti. Ti permette di riconoscere subito chi chiama o scrive, anche quando il numero non è noto.",
    gate: "on",
    points: [
      "Nome e numero di ogni contatto",
      "Contatti preferiti evidenziati",
      "Fino a 500 contatti sincronizzati",
      "Integrato con registro chiamate e messaggi",
    ],
  },
  {
    key: "web_history",
    icon: "globe",
    tag: "Web",
    title: "Siti visitati",
    short: "Cronologia di navigazione reale.",
    detail:
      "Elenco dei siti visitati con URL, durata della visita e ricerche, letti da Chrome, Edge, Samsung Internet, Opera e Firefox. Non servono i file privati del browser.",
    gate: "on",
    points: [
      "URL, durata e ricerche",
      "Multi-browser (Chrome, Edge, Samsung, Opera, Firefox)",
      "Nessun accesso ai dati privati del browser",
      "Sempre consultabile dal pannello",
    ],
  },
  {
    key: "app_usage",
    icon: "activity",
    tag: "App",
    title: "Utilizzo delle app",
    short: "Quanto tempo su ogni app.",
    detail:
      "Tempo di utilizzo per applicazione, app più usate della giornata e report periodici per capire le abitudini digitali.",
    gate: "on",
    points: [
      "Tempo per app e totale giornaliero",
      "Classifica app più usate",
      "Report periodici",
      "Base per limiti e regole",
    ],
  },
  {
    key: "keylogger",
    icon: "keyboard",
    tag: "Tastiera",
    title: "Eventi di digitazione",
    short: "Cosa viene digitato (opt-in).",
    detail:
      "Registrazione degli eventi di digitazione (opt-in). È la capacità più sensibile: resta disattiva finché il genitore non la abilita esplicitamente per il singolo dispositivo.",
    gate: "opt",
    sensitive: true,
    points: [
      "Eventi di digitazione catturati",
      "Off di default, opt-in esplicito",
      "Per-device: la accendi solo dove serve",
      "Sempre disattivabile dal pannello",
    ],
  },
];

// Strumenti "live" on-demand, presentati come blocco dedicato (stile
// "Remote Safety Tools" di ClevGuard): partono solo quando li invia il genitore.
// Ogni voce corrisponde a un comando realmente gestito dal client Android
// (GuardianForegroundService.handleCommand): NON aggiungere comandi che il
// client risponde NOT_IMPLEMENTED (block_app, lock, app_block, sync_rules...).
export const LIVE_TOOLS = [
  {
    key: "live_video",
    icon: "video",
    title: "Diretta video",
    desc: "Stream live dalla fotocamera, con fps e frame in tempo reale.",
    command: "stream_start / stream_stop",
  },
  {
    key: "video_record",
    icon: "film",
    title: "Registrazione video",
    desc: "Registra un video dalla camera del dispositivo, fronte o retro, con qualità e durata scelte da te.",
    command: "video_start / video_stop",
  },
  {
    key: "photo_capture",
    icon: "camera",
    title: "Foto segreta",
    desc: "Accende la camera e scatta una foto immediata, anche con lo schermo spento.",
    command: "photo_capture",
  },
  {
    key: "screenshots",
    icon: "monitor",
    title: "Screenshot remoto",
    desc: "Cattura lo schermo in quell'istante: vedi cosa sta guardando, chattando o giocando.",
    command: "screenshot",
  },
  {
    key: "live_audio",
    icon: "audio-lines",
    title: "Ascolto live",
    desc: "Microfono in streaming su comando: continua anche durante una chiamata.",
    command: "audio_stream_start / stop",
  },
  {
    key: "ambient_audio",
    icon: "mic",
    title: "Registrazione ambientale",
    desc: "Registra l'ambiente intorno al telefono, su comando o a intervalli programmati.",
    command: "audio_start / audio_stop",
  },
  {
    key: "location",
    icon: "locate",
    title: "Posizione immediata",
    desc: "Richiedi il punto GPS esatto adesso, con conferma di esecuzione.",
    command: "location_now",
  },
  {
    key: "switch_camera",
    icon: "switch-camera",
    title: "Cambio camera",
    desc: "Passi da fronte a retro durante una diretta, senza interrompere lo stream.",
    command: "switch_camera",
  },
];

// Comandi di controllo del dispositivo: anche questi esistono nel client
// (vibrate, ring, send_sms, enable_hotspot, open_app, navigate_to, ping).
// È la parte "attiva" del prodotto: non solo osservi, ma intervieni.
export const CONTROL_COMMANDS = [
  {
    icon: "bell-ring",
    title: "Fai squillare il telefono",
    desc: "Fa suonare la suoneria al volume che decidi, anche se il telefono è in silenzioso. Utile per ritrovarlo.",
    command: "ring / play_sound",
  },
  {
    icon: "vibrate",
    title: "Vibrazione remota",
    desc: "Fa vibrare il dispositivo per un tempo scelto da te, come segnale discreto concordato con tuo figlio.",
    command: "vibrate",
  },
  {
    icon: "send",
    title: "Invia un SMS",
    desc: "Invia un messaggio di testo dal dispositivo associato a un numero che indichi tu.",
    command: "send_sms",
  },
  {
    icon: "wifi",
    title: "Attiva l'hotspot",
    desc: "Accende il punto di accesso Wi-Fi del telefono per mantenerlo raggiungibile anche senza dati attivi.",
    command: "enable_hotspot",
  },
  {
    icon: "app-window",
    title: "Apri un'app da remoto",
    desc: "Avvia un'applicazione specifica sul dispositivo, senza che nessuno tocchi il telefono.",
    command: "open_app",
  },
  {
    icon: "navigation",
    title: "Invia una destinazione",
    desc: "Apre la navigazione verso un indirizzo sul dispositivo associato.",
    command: "navigate_to",
  },
  {
    icon: "activity",
    title: "Verifica se è online",
    desc: "Un ping immediato che ti dice se il dispositivo è vivo e connesso in questo momento.",
    command: "ping",
  },
  {
    icon: "shield-check",
    title: "Conferma di esecuzione",
    desc: "Ogni comando ti restituisce lo stato reale: inviato, consegnato, eseguito o fallito. Nessuna ambiguità.",
    command: "ack",
  },
];

// Funzioni di controllo/sicurezza trasversali (non sono "dati intercettati"
// ma capacità del prodotto) — derivano da comandi remoti, E2E e stealth camera.
export const PLATFORM_FEATURES = [
  {
    key: "e2e_encryption",
    icon: "lock",
    tag: "Sicurezza",
    title: "Crittografia end-to-end",
    short: "I contenuti sensibili viaggiano cifrati: li leggi solo tu.",
    gate: "on",
  },
  {
    key: "remote_commands",
    icon: "zap",
    tag: "Remoto",
    title: "Controllo remoto",
    short: "Invii comandi al dispositivo e vedi la conferma di esecuzione in tempo reale.",
    gate: "on",
  },
  {
    key: "stealth",
    icon: "eye-off",
    tag: "Cattura",
    title: "Cattura silenziosa",
    short: "Foto e video in background senza interfaccia visibile (opt-in, la funzione più invasiva).",
    gate: "opt",
    sensitive: true,
  },
  {
    key: "realtime",
    icon: "bell",
    tag: "Realtime",
    title: "Notifiche in tempo reale",
    short: "Avvisi immediati su eventi e attività, direttamente sulla dashboard VIP.",
    gate: "on",
  },
];

// Sezione compatibilità (come la tabella "Compatibility" di ClevGuard).
// Valori derivati dal build.gradle reale: minSdk 24 = Android 7.0 Nougat.
export const COMPATIBILITY = [
  { label: "Sistema operativo", value: "Android 7.0 (Nougat) e superiori" },
  { label: "Root richiesto", value: "No — funziona senza root" },
  { label: "Distribuzione", value: "APK VIP scaricato dopo l'acquisto (non Google Play)" },
  { label: "Dispositivi per account", value: "Più figli / più dispositivi associati" },
  { label: "Dashboard", value: "Browser su smartphone, tablet o computer" },
  { label: "Marche testate", value: "Samsung, Xiaomi, OPPO, Realme, TCL e altri Android" },
];

// Passi di attivazione (VIP: account → Stripe → APK).
export const SETUP_STEPS = [
  {
    n: "01",
    icon: "user-plus",
    title: "Crea l'account VIP",
    desc: "Registrati sul sito VIP. L'account è separato da quello Standard / Google Play.",
  },
  {
    n: "02",
    icon: "credit-card",
    title: "Abbonati con Stripe — 59,99 €/mese",
    desc: "Pagamento sicuro con Stripe. Non è un acquisto in-app su Google Play.",
  },
  {
    n: "03",
    icon: "download",
    title: "Installa l'APK sul dispositivo",
    desc: "Dopo il pagamento scarichi e installi l'APK VIP, associ il dispositivo e inizi a monitorare dalla dashboard.",
  },
];

// Fascia prezzo esposta in home (stile ClevGuard: prezzo + cosa include).
export const PRICING = {
  monthly: "59,99 €",
  per: "al mese",
  billing: "Pagamento e rinnovo gestiti da Stripe",
  includes: [
    "Tutte le capacità di monitoraggio VIP",
    "Account e dashboard dedicati",
    "APK Android (installazione guidata)",
    "Più dispositivi per famiglia",
    "Crittografia end-to-end",
    "Aggiornamenti inclusi",
  ],
};
